import { Component, ViewChild, ElementRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { InvoiceService, Invoice, PendingPaymentResponse } from '../services/invoice.service';
import { InvoiceDetailModalComponent } from './invoice-detail-modal.component';
import { UserStorageService } from '../services/user-storage.service';
import { PaymentDataModalComponent } from './payment-data-modal.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { UserPermissionsService } from '../services/user-permissions.service';
import { HasPermissionDirective } from '../directives/has-permission.directive';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-invoice-list',
  standalone: true,
  imports: [CommonModule, HasPermissionDirective, TranslocoModule],
  templateUrl: './invoice-list.component.html',
  styleUrl: './invoice-list.component.css'
})
export class InvoiceListComponent implements OnInit {
  @ViewChild('invoicesContainer') invoicesContainer!: ElementRef;
  
  invoices: Invoice[] = [];
  
  // Variáveis para controle de paginação e infinite scroll
  skip: number = 0;
  limit: number = 30;
  totalInvoices: number = 0;
  loadingInvoices: boolean = false;
  hasMoreInvoices: boolean = true;
  
  // Map para armazenar IdempotencyKey por invoice (GUID único por card)
  private idempotencyKeys: Map<string, string> = new Map();

  constructor(
    private invoiceService: InvoiceService,
    private dialog: MatDialog,
    private userStorageService: UserStorageService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private permissionsService: UserPermissionsService,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    this.loadInvoices();
  }

  loadInvoices(): void {
    if (this.loadingInvoices || !this.hasMoreInvoices) {
      return;
    }

    this.loadingInvoices = true;
    this.invoiceService.getInvoices(this.skip, this.limit)
      .subscribe({
        next: (response) => {
          const items = response.items || [];
          this.totalInvoices = response.total || 0;
          
          // Gerar IdempotencyKey (GUID) para cada invoice quando renderizar
          items.forEach(invoice => {
            if (!this.idempotencyKeys.has(invoice.id)) {
              this.idempotencyKeys.set(invoice.id, this.generateGuid());
            }
          });
          
          // Adicionar novos itens à lista
          if (this.skip === 0) {
            this.invoices = [...items];
          } else {
            this.invoices = [...this.invoices, ...items];
          }
          
          // Atualizar controle de paginação
          this.skip += items.length;
          this.hasMoreInvoices = this.invoices.length < this.totalInvoices;
          this.loadingInvoices = false;

          // Verificar se precisa carregar mais faturas automaticamente (se não há scroll suficiente)
          setTimeout(() => {
            this.checkIfNeedMoreInvoices();
          }, 100);
        },
        error: (error) => {
          console.error('Erro ao carregar faturas:', error);
          this.loadingInvoices = false;
        }
      });
  }

  private checkIfNeedMoreInvoices(): void {
    if (!this.invoicesContainer || !this.hasMoreInvoices || this.loadingInvoices) {
      return;
    }

    const container = this.invoicesContainer.nativeElement;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Se o conteúdo não ultrapassa a altura do container, carregar mais faturas
    if (scrollHeight <= clientHeight && this.hasMoreInvoices) {
      this.loadInvoices();
    }
  }
  

  onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target) return;
    
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    
    // Verificar se chegou próximo ao final (dentro de 50px)
    const scrollBottom = scrollHeight - scrollTop - clientHeight;
    if (scrollBottom <= 50 && this.hasMoreInvoices && !this.loadingInvoices) {
      this.loadInvoices();
    }
  }

  getStatusText(status: number): string {
    const statusMap: { [key: number]: string } = {
      0: this.transloco.translate('invoices.status.pending'),
      1: this.transloco.translate('invoices.status.issued'),
      2: this.transloco.translate('invoices.status.paid'),
      3: this.transloco.translate('invoices.status.cancelled')
    };
    return statusMap[status] || this.transloco.translate('invoices.status.unknown');
  }

  getStatusClass(status: number): string {
    const classMap: { [key: number]: string } = {
      0: 'bg-yellow-100 text-yellow-800',
      1: 'bg-blue-100 text-blue-800',
      2: 'bg-green-100 text-green-800',
      3: 'bg-gray-100 text-gray-800'
    };
    return classMap[status] || 'bg-gray-100 text-gray-800';
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  isOverdue(invoice: Invoice): boolean {
    return invoice.isOverdue === true;
  }

  openInvoiceDetail(invoice: Invoice): void {
    // Verificar permissão para visualizar faturas
    if (!this.permissionsService.hasPermission('invoices.view')) {
      this.toastr.error(this.transloco.translate('invoices.toast.noViewPermission'), this.transloco.translate('toasts.accessDenied'));
      return;
    }

    // Obter ou gerar IdempotencyKey para este invoice
    let idempotencyKey = this.idempotencyKeys.get(invoice.id);
    if (!idempotencyKey) {
      idempotencyKey = this.generateGuid();
      this.idempotencyKeys.set(invoice.id, idempotencyKey);
    }
    
    const dialogRef = this.dialog.open(InvoiceDetailModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      data: { invoice, idempotencyKey },
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      // Se a modal foi fechada após verificar o QR code e o pagamento foi confirmado, recarregar faturas
      if (result?.shouldReload) {
        this.reloadInvoices();
      }
    });
  }

  canPay(invoice: Invoice): boolean {
    // Verificar se o usuário tem permissão para pagar faturas
    if (!this.permissionsService.hasPermission('invoices.pay')) {
      return false;
    }
    const status = Number(invoice.status);
    return status === 0 || status === 1;
  }

  payNow(event: Event, invoice: Invoice): void {
    event.stopPropagation(); // Prevenir que o clique abra o modal de detalhes

    // Verificar permissão antes de permitir pagamento
    if (!this.permissionsService.hasPermission('invoices.pay')) {
      this.toastr.error(this.transloco.translate('invoices.toast.noPayPermission'), this.transloco.translate('toasts.accessDenied'));
      return;
    }

    // Verificar se já existe um PIX pendente antes de abrir a modal de dados
    this.spinner.show();
    this.invoiceService.getPendingPayment(invoice.id)
      .subscribe({
        next: (response: PendingPaymentResponse) => {
          this.spinner.hide();
          if (response.hasPendingPayment && response.pixResponse) {
            // Se existe PIX pendente, abrir diretamente o modal de detalhes com o PIX já carregado
            let idempotencyKey = this.idempotencyKeys.get(invoice.id);
            if (!idempotencyKey) {
              idempotencyKey = this.generateGuid();
              this.idempotencyKeys.set(invoice.id, idempotencyKey);
            }

            const dialogRef = this.dialog.open(InvoiceDetailModalComponent, {
              width: '800px',
              maxWidth: '90vw',
              data: { 
                invoice,
                pixResponse: response.pixResponse,
                showingQrCode: true,
                idempotencyKey: idempotencyKey
              },
              disableClose: false
            });

            dialogRef.afterClosed().subscribe(result => {
              if (result?.shouldReload) {
                this.reloadInvoices();
              }
            });
          } else {
            // Se não existe PIX pendente, seguir o fluxo normal (abrir modal de dados)
            const userData = this.userStorageService.getUserData();

            const dialogRef = this.dialog.open(PaymentDataModalComponent, {
              width: '500px',
              maxWidth: '90vw',
              data: userData ? {
                email: userData.email,
                firstName: userData.firstName,
                lastName: userData.lastName
              } : {}
            });

            dialogRef.afterClosed().subscribe(result => {
              if (result) {
                // Salva os dados no localStorage após confirmação
                this.userStorageService.saveUserData(result.email, result.firstName, result.lastName);
                this.generatePixAndOpenModal(invoice, result.email, result.firstName, result.lastName);
              }
            });
          }
        },
        error: (error) => {
          console.error('Erro ao verificar pagamento pendente:', error);
          this.spinner.hide();
          // Em caso de erro, seguir o fluxo normal (abrir modal de dados)
          const userData = this.userStorageService.getUserData();

          const dialogRef = this.dialog.open(PaymentDataModalComponent, {
            width: '500px',
            maxWidth: '90vw',
            data: userData ? {
              email: userData.email,
              firstName: userData.firstName,
              lastName: userData.lastName
            } : {}
          });

          dialogRef.afterClosed().subscribe(result => {
            if (result) {
              // Salva os dados no localStorage após confirmação
              this.userStorageService.saveUserData(result.email, result.firstName, result.lastName);
              this.generatePixAndOpenModal(invoice, result.email, result.firstName, result.lastName);
            }
          });
        }
      });
  }

  private generatePixAndOpenModal(invoice: Invoice, email: string, firstName: string, lastName: string): void {
    this.spinner.show();
    
    // Obter ou gerar IdempotencyKey para este invoice
    let idempotencyKey = this.idempotencyKeys.get(invoice.id);
    if (!idempotencyKey) {
      idempotencyKey = this.generateGuid();
      this.idempotencyKeys.set(invoice.id, idempotencyKey);
    }
    
    this.invoiceService.generatePix(invoice.id, email, firstName, lastName, idempotencyKey)
      .subscribe({
        next: (response) => {
          this.spinner.hide();
          // Abrir modal de detalhes com QR Code já gerado
          const dialogRef = this.dialog.open(InvoiceDetailModalComponent, {
            width: '800px',
            maxWidth: '90vw',
            data: { 
              invoice,
              pixResponse: response,
              showingQrCode: true,
              idempotencyKey: idempotencyKey
            },
            disableClose: false
          });

          dialogRef.afterClosed().subscribe(result => {
            // Se a modal foi fechada após verificar o QR code e o pagamento foi confirmado, recarregar faturas
            if (result?.shouldReload) {
              this.reloadInvoices();
            }
          });
        },
        error: (error) => {
          console.error('Erro ao gerar PIX:', error);
          this.spinner.hide();
          this.toastr.error(this.transloco.translate('invoices.toast.qrError'), this.transloco.translate('toasts.error'));
        }
      });
  }

  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  private reloadInvoices(): void {
    // Resetar paginação e recarregar faturas do início
    this.skip = 0;
    this.invoices = [];
    this.hasMoreInvoices = true;
    this.loadInvoices();
  }
}

